import {jsPDF} from 'jspdf';
import {ClientModel} from '../models/client-returns-data.model';
import {ReturnFormLabels} from './return-document.util';

declare const JsBarcode: any;

export interface ReturnFormPdfItem {
	article_code?: string;
	sku?: string;
	quantity: number;
	return_reason?: string;
	return_reason_description?: string;
	damage_description?: string;
}

export interface ReturnFormPdfInput {
	brand: string;
	returnNumber: string | number;
	orderNumber: string;
	returnDate: string;
	client: ClientModel;
	items: ReturnFormPdfItem[];
	labels: ReturnFormLabels;
}

export async function buildReturnFormPdfBytes(input: ReturnFormPdfInput): Promise<Uint8Array> {
	const doc = new jsPDF({unit: 'mm', format: 'a4', compress: true});
	const pageWidth = 210;
	const pageHeight = 297;
	const margin = 8;
	let y = 0;

	const logoDataUrl = await fetchAssetAsDataUrl(`assets/img/${input.brand}_white.png`);
	const barcodeDataUrl = buildBarcodeDataUrl(input.orderNumber);
	const returnAddressLines = buildReturnAddressLines(input.client.address.country);

	doc.setDrawColor(0);
	doc.setTextColor(0);
	doc.setFont('helvetica', 'normal');

	doc.setFillColor(0, 0, 0);
	doc.rect(0, 0, pageWidth, 22, 'F');
	if (logoDataUrl) {
		const logoWidth = 38;
		const logoHeight = 10;
		doc.addImage(logoDataUrl, 'PNG', (pageWidth - logoWidth) / 2, 5, logoWidth, logoHeight);
	}

	y = 31.5;
	doc.setFontSize(20.5);
	doc.setFont('helvetica', 'normal');
	doc.text(input.labels.returnFormText, pageWidth / 2, y, {align: 'center'});

	y = 47;
	doc.setFontSize(10);
	doc.setFont('helvetica', 'bold');
	doc.text(`${input.labels.returnNumber}:`, margin, y);
	doc.text(`${input.labels.returnDate}:`, 124, y);
	doc.text(`${input.labels.orderNumber}:`, margin, y + 5.2);
	doc.setFont('helvetica', 'normal');
	doc.text(String(input.returnNumber), margin + 33, y);
	doc.text(String(input.returnDate), 158, y);
	doc.text(String(input.orderNumber), margin + 31, y + 5.2);
	doc.setDrawColor(230);
	doc.line(margin, y + 8, pageWidth - margin, y + 8);

	y = 72;
	if (barcodeDataUrl) {
		doc.addImage(barcodeDataUrl, 'PNG', margin, y, 57, 18);
		doc.setFont('helvetica', 'normal');
		doc.setFontSize(10.5);
		doc.text(String(input.orderNumber), margin + 28.5, y + 22.5, {align: 'center'});
	}

	y = 101;
	doc.setFontSize(14.2);
	doc.setFont('helvetica', 'bold');
	doc.text(input.labels.returnAddress, margin, y);
	doc.text(input.labels.sender, 110, y);

	doc.setFontSize(10.4);
	doc.setFont('helvetica', 'normal');
	y += 9.5;
	returnAddressLines.forEach((line, index) => {
		doc.text(line, margin, y + index * 5.7);
	});

	const senderLines = buildSenderLines(input.client);
	senderLines.forEach((line, index) => {
		doc.text(line, 110, y + index * 5.7);
	});

	y = 145;
	doc.setFontSize(14.2);
	doc.setFont('helvetica', 'bold');
	doc.text(input.labels.itemsHeader, margin, y);

	y += 7.5;
	y = drawItemsTable(doc, input.items, input.labels, y, pageWidth, margin);

	y += 7.5;
	doc.setFontSize(14.2);
	doc.setFont('helvetica', 'bold');
	doc.text(input.labels.returnInstructionsHeader, margin, y);
	y += 5.5;
	doc.setFontSize(10.4);
	doc.setFont('helvetica', 'normal');
	doc.text(input.labels.instructionLeadIn, margin, y);
	y += 10.5;
	doc.setFont('helvetica', 'bold');
	doc.text(input.labels.instructionIncludeForm, margin, y);
	y += 10.5;
	doc.text(input.labels.instructionPrintLabel, margin, y);
	y += 10.5;
	doc.setFont('helvetica', 'normal');
	const notificationLines = doc.splitTextToSize(input.labels.instructionEmailNotification, pageWidth - margin * 2);
	doc.text(notificationLines, margin, y);

	const footerY = pageHeight - 12;
	doc.setFillColor(0, 0, 0);
	doc.rect(0, footerY, pageWidth, 12, 'F');
	doc.setTextColor(255, 255, 255);
	doc.setFontSize(9);
	doc.setFont('helvetica', 'normal');
	doc.text(input.labels.includeForm, pageWidth / 2, footerY + 8, {align: 'center'});

	return new Uint8Array(doc.output('arraybuffer'));
}

function drawItemsTable(doc: jsPDF, items: ReturnFormPdfItem[], labels: ReturnFormPdfInput['labels'], startY: number, pageWidth: number, margin: number): number {
	const tableX = margin;
	const tableWidth = pageWidth - margin * 2;
	const widths = [45, 16, 24, tableWidth - 45 - 16 - 24];
	const headerHeight = 11;
	let y = startY;

	const ensureSpace = (height: number) => {
		if (y + height > 268) {
			doc.addPage();
			y = 20;
			doc.setTextColor(0, 0, 0);
			doc.setFont('helvetica', 'bold');
			doc.setFontSize(15);
			doc.text(labels.itemsHeader, margin, y);
			y += 8;
			drawTableHeader(doc, labels, tableX, y, widths, headerHeight);
			y += headerHeight;
		}
	};

	drawTableHeader(doc, labels, tableX, y, widths, headerHeight);
	y += headerHeight;

	items.forEach((item) => {
		const article = item.sku || item.article_code || '';
		const qty = String(item.quantity ?? '');
		const reason = normalizePrintableText(item.return_reason || '');
		const translatedReason = labels.translateReturnReason(reason);
		const extraDescription = [
			normalizePrintableText(item.damage_description),
			normalizePrintableText(item.return_reason_description)
		].filter(Boolean).join('\n');
		const description = [translatedReason, extraDescription].filter(Boolean).join('\n');
		const descriptionLines = doc.splitTextToSize(description || labels.reasonForReturn, widths[3] - 2);
		const rowHeight = Math.max(11, descriptionLines.length * 5 + 2);
		ensureSpace(rowHeight + 2);

		doc.setDrawColor(220);
		doc.rect(tableX, y, widths[0], rowHeight);
		doc.rect(tableX + widths[0], y, widths[1], rowHeight);
		doc.rect(tableX + widths[0] + widths[1], y, widths[2], rowHeight);
		doc.rect(tableX + widths[0] + widths[1] + widths[2], y, widths[3], rowHeight);

		doc.setFont('helvetica', 'normal');
		doc.setFontSize(9.8);
		doc.text(article, tableX + 1, y + 6.5);
		doc.text(qty, tableX + widths[0] + 1, y + 6.5);
		doc.text(reason, tableX + widths[0] + widths[1] + 1, y + 6.5);
		doc.text(descriptionLines, tableX + widths[0] + widths[1] + widths[2] + 1, y + 6.5);
		y += rowHeight;
	});

	return y;
}

function drawTableHeader(doc: jsPDF, labels: ReturnFormPdfInput['labels'], x: number, y: number, widths: number[], height: number) {
	const headers = [labels.articleName, labels.quantity, labels.reasonForReturn, labels.returnDescription];
	doc.setFont('helvetica', 'bold');
	doc.setFontSize(9.4);
	doc.setFillColor(249, 249, 249);
	doc.rect(x, y, widths.reduce((sum, value) => sum + value, 0), height, 'F');
	let currentX = x;
	headers.forEach((header, index) => {
		doc.setDrawColor(220);
		doc.rect(currentX, y, widths[index], height);
		const lines = doc.splitTextToSize(header, widths[index] - 2);
		doc.text(lines, currentX + 1, y + 4.9);
		currentX += widths[index];
	});
}

function buildReturnAddressLines(country: string) {
	return country?.toUpperCase() === 'CH'
		? ['AstorMueller AG', 'c/o SwissCommerce', 'Chasseralstrasse 3', '4900 Langenthal', 'Switzerland']
		: ['AstorMueller AG c/o Meyer & Meyer', 'Logistikzentrum Peine GmbH', 'Lehmkuhlenweg 700', '31224 Peine', 'Deutschland'];
}

function buildSenderLines(client: ClientModel) {
	return [
		normalizePrintableText(`${client.firstname} ${client.lastname}`),
		normalizePrintableText(`${client.address?.street ?? ''} ${client.address?.number ?? ''}`),
		normalizePrintableText(`${client.address?.postcode ?? ''} ${client.address?.city ?? ''}`)
	].filter(Boolean);
}

function normalizePrintableText(value: string | null | undefined) {
	return String(value ?? '')
		.replace(/[\u200B-\u200D\u2060\uFEFF]/g, '')
		.replace(/\s+/g, ' ')
		.trim();
}

function buildBarcodeDataUrl(text: string) {
	const canvas = document.createElement('canvas');
	JsBarcode(canvas, text, {
		format: 'CODE128',
		height: 62,
		width: 2.05,
		displayValue: false,
		text,
		margin: 0,
	});
	return canvas.toDataURL('image/png');
}

async function fetchAssetAsDataUrl(assetPath: string) {
	try {
		const response = await fetch(assetPath);
		if (!response.ok) {
			return null;
		}
		const blob = await response.blob();
		return await blobToDataUrl(blob);
	} catch {
		return null;
	}
}

function blobToDataUrl(blob: Blob): Promise<string> {
	return new Promise((resolve, reject) => {
		const reader = new FileReader();
		reader.onloadend = () => resolve(String(reader.result));
		reader.onerror = reject;
		reader.readAsDataURL(blob);
	});
}
